<template>
  <div
    class="el-autocomplete"
    v-clickoutside="close"
    aria-haspopup="listbox"
    role="combobox"
    :aria-expanded="suggestionVisible"
    :aria-owns="id"
  >
    <el-input
      ref="input"
      :data-class="dataClass"
      v-bind="[$props, $attrs]"
      :prefix-icon="loadingIcon"
      @input="handleInput"
      @change="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
      @clear="handleClear"
      @keydown.up.native.prevent="beforeUp"
      @keydown.down.native.prevent="beforeDown"
      @keydown.enter.native.prevent="handleKeyEnter"
      @keydown.native.tab="close"
    >
      <template slot="prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </template>
      <template slot="append" v-if="$slots.append">
        <slot name="append"></slot>
      </template>
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template>
    </el-input>

    <mg-autocomplete-suggestions
      v-if="!hideSuggestion"
      visible-arrow
      :class="[popperClass ? popperClass : '']"
      class="mg-popper-autocomplete"
      :popper-options="popperOptions"
      :append-to-body="popperAppendToBody"
      ref="suggestions"
      :placement="placement"
      :id="id"
    >
      <div class="mg-autocomplate-th">
        <slot name="header"></slot>
      </div>
      <!-- 无数据时显示空 -->
      <div v-if="showEmptyText" class="mg-autocomplete-suggestion__empty">{{ $MGUI.i18n.t('暂无数据') }}</div>
      <li
        v-for="(item, index) in suggestions"
        :key="index"
        class="mg-autocomplate-td"
        :class="{ highlighted: highlightedIndex === index }"
        @click="(e) => selectItem(e, item)"
        :id="`${id}-item-${index}`"
        role="option"
        :aria-selected="highlightedIndex === index"
      >
        <slot :item="item">
          {{ item[valueKey] }}
        </slot>
      </li>
    </mg-autocomplete-suggestions>

    <el-dialog
      v-else
      top="8vh"
      width="80%"
      custom-class="mg-fast-add-goods-dialog"
      :append-to-body="true"
      :visible.sync="suggestionsDialogVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :destroy-on-close="true"
      @opened="onSuggestionsDialogOpened"
      @closed="onSuggestionsDialogClosed"
    >
      <span slot="title">
        <el-input
          ref="searchRef"
          v-model="secondSearchValue"
          style="width: 400px"
          prefix-icon="el-icon-search"
          clearable
          :placeholder="$MGUI.i18n.t('SKU编码/名称/货号/助记码/条码')"
          @keydown.enter.native.stop="handleInputEnter"
        />
        <span class="suggestion-dialog__tips">
          {{ $MGUI.i18n.t('点击[Tab切换]进行输入；点击[Enter回车]进行查询') }}
        </span>
        <el-button v-if="searchMoreGoods" class="search-more-goods-btn" type="primary" @click="onSearchMore">
          {{ $MGUI.i18n.t('搜索更多商品[F7]') }}
        </el-button>
        <el-button v-if="continueAdd" class="search-more-goods-btn" type="primary" @click="onContinueAdd">
          {{ $MGUI.i18n.t('继续新增') }}
        </el-button>
      </span>
      <div class="flex flex-col" style="height: calc(100% - 12px)">
        <mg-vxe-table
          style="height: 1px; margin-bottom: 0"
          wrapper-height="100%"
          ref="mgVxeTable"
          :loading="tableLoading"
          :columns="tableColumns"
          :show-mini-pagination="false"
          :is-show-top-bar="searchMoreGoods"
          :configurable="false"
          :initial-data="suggestions"
          :sort-remote="false"
          :multiple-sort="true"
          :business-type="tableBusinessType"
          is-current-row
          :row-config="{
            keyField: 'skuId',
          }"
          @cell-click="onRowClick"
          @cell-dblclick="onRowDbclick"
          :empty-text="$MGUI.i18n.t(`查询内容“{value}”暂无数据`, { value: $refs?.input?.getInput()?.value })"
        ></mg-vxe-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import { Autocomplete } from 'element-ui'
  import MgAutocompleteSuggestions from './autocomplete-suggestions.vue'
  import { WebFunnyEventCode } from '@/constants'
  import { sendWebFunnyEvent } from '@/utils'
  import dialogShortcuts from '@/mixins/dialogShortcuts'

  // 覆盖 element-ui 的 beforeDestroy，避免报错
  Autocomplete.beforeDestroy = () => {}

  export default {
    extends: Autocomplete,

    name: 'MgAutocomplete',

    mixins: [dialogShortcuts],

    props: {
      // 当匹配到一条数据时，是否直接确认选中
      selectWhenMatchedOne: {
        type: Boolean,
        default: false,
      },

      // 输入长度小于 limit 时不触发搜索
      limit: {
        type: Number,
        default: 3,
      },

      dataClass: {
        type: String,
        default: '',
      },

      // 是否隐藏搜索建议
      hideSuggestion: Boolean,

      // 表格记忆列宽
      tableBusinessType: String,

      columns: {
        type: Array,
        default: () => [],
      },

      searchModes: {
        type: Object,
        default: () => {},
      },

      searchMoreGoods: Boolean,
      continueAdd: Boolean,
      // 是否可以编辑（用于控制是否允许选择商品）
      canEdit: {
        type: Boolean,
        default: true,
      },
      /** true：大弹窗快捷键走 ShortcutManager dialog 层（newCashier 经 fast-add 传入） */
      useShortcutCenter: {
        type: Boolean,
        default: false,
      },
    },

    components: {
      MgAutocompleteSuggestions,
    },

    mounted() {
      // 将弹窗搜索区域内的 input 添加 className，便于过滤
      const inputEl = this.$refs.searchRef?.$el.querySelectorAll('.el-input__inner')
      if (inputEl?.length) {
        inputEl.forEach((el) => {
          el.classList.add('suggestion-dialog__input')
        })
      }
    },

    data() {
      return {
        showEmptyText: false,
        showLoadingIcon: false,
        currentInputValue: '',

        // 商品弹窗中的一些属性
        suggestionsDialogVisible: false,
        secondSearchValue: '',
        currentSecondSearchValue: '',
        isSelected: false,
        tableLoading: false,
      }
    },

    computed: {
      suggestionVisible() {
        const suggestions = this.suggestions
        let isValidData = Array.isArray(suggestions) && suggestions.length > 1
        if (this.showEmptyText) return true
        return (isValidData || this.loading) && this.activated
      },
      loadingIcon() {
        return this.showLoadingIcon ? 'el-icon-loading is-loading' : 'el-icon-search'
      },
      tableColumns() {
        return this.columns.map((item) => ({ ...item, sortable: true }))
      },
    },

    watch: {
      suggestions(list) {
        if (list.length) {
          this.$nextTick(() => {
            this.highlightRow(0)
          })
        }
      },
    },

    methods: {
      closeClear() {
        this.currentInputValue = ''
        this.showEmptyText = false
        this.loading = false
        this.showLoadingIcon = false
        this.suggestionDisabled = true
        this.suggestions = []
        this.highlightedIndex = -1
      },

      getData(queryString, ifScanGun) {
        if (!this.hideSuggestion && this.suggestionDisabled) {
          this.showLoadingIcon = false
          return
        }
        this.loading = false

        // 若弹窗展示，则增加额外参数
        const secondSearch = this.hideSuggestion ? { isFrame: true, searchKeyTwo: this.secondSearchValue } : {}

        // 搜索模式 若关键词为中文字符，则强制判定为自动搜索
        const chineseRegex = /[\u4e00-\u9fa5\uf900-\ufa2d\u2e80-\u2eff\u3000-\u303f\uff00-\uffef]/g

        const searchModes = chineseRegex.test(queryString) || !ifScanGun ? {} : this.searchModes
        this.fetchSuggestions(
          queryString,
          (suggestions) => {
            this.loading = false
            this.showLoadingIcon = false
            this.tableLoading = false
            if (!this.hideSuggestion && this.suggestionDisabled) {
              return
            }
            if (Array.isArray(suggestions)) {
              this.showEmptyText = suggestions.length === 0
              this.suggestions = suggestions
              this.highlightedIndex = this.highlightFirstItem ? 0 : -1
              if (suggestions.length === 0 && this.searchMoreGoods && !this.secondSearchValue) {
                this.onSearchMore()
                return
              }

              if (suggestions.length === 1 && this.selectWhenMatchedOne && !this.suggestionsDialogVisible) {
                this.selectOne()
                return
              }

              // 若弹窗展示且不存在搜索更多商品逻辑，没有查到数据，则confirm弹窗
              if (this.hideSuggestion && !this.searchMoreGoods && suggestions.length === 0) {
                this.$mgConfirm(this.$MGUI.i18n.t('暂无此商品！'), this.$MGUI.i18n.t('提示'), {
                  showCancelButton: false,
                  closeOnClickModal: true,
                })
                return
              }

              this.suggestionsDialogVisible = this.hideSuggestion
            } else {
              throw new Error('[Element Error][Autocomplete]autocomplete suggestions must be an array')
            }
          },
          { secondSearch, searchModes }
        )
      },

      selectItem(e, item) {
        // 核心代码
        this.$refs.input.focus()
        this.select(item)
        this.$el.dispatchEvent(
          new KeyboardEvent('keydown', {
            bubbles: true,
            type: 'keydown',
            code: 'Enter',
            key: 'Enter',
            keyCode: 13,
            which: 13,
          })
        )
      },

      close() {
        this.showEmptyText = false
        this.activated = false
      },

      beforeUp(e) {
        if (this.suggestions?.length > 0) {
          this.highlight(this.highlightedIndex - 1)
          return
        }
        this.$emit('up', e)
      },

      beforeDown(e) {
        if (this.suggestions?.length > 0) {
          this.highlight(this.highlightedIndex + 1)
          return
        }
        this.$emit('down', e)
      },

      handleInput(value) {
        this.$emit('input', value)
        this.suggestionDisabled = false
        if (!this.triggerOnFocus && !value) {
          this.suggestionDisabled = true
          this.showEmptyText = false
          this.suggestions = []
          return
        }
      },

      selectOne() {
        const len = this.suggestions?.length
        if (len > 0 && this.highlightedIndex >= 0 && this.highlightedIndex < len) {
          this.select(this.suggestions[this.highlightedIndex])
        }
      },

      /**
       * 回车添加商品
       * @param {*} e
       */
      handleKeyEnter(e) {
        if (this.isSelected) {
          sendWebFunnyEvent(WebFunnyEventCode.PAGE, {
            actionType: `快速添加-选择弹框-选择第${this.highlightedIndex + 1}个商品`,
          })
        } else {
          sendWebFunnyEvent(WebFunnyEventCode.PAGE, {
            actionType: `快速添加-${e.ifScanGun ? '扫码' : '搜索'}(${e.target.value})`,
          })
        }
        this.$refs.input.getInput().select()
        const len = this.suggestions?.length
        const value = e.target.value
        if (value.length < this.limit) return
        if (this.currentInputValue === value && len > 0 && this.highlightedIndex >= 0 && this.highlightedIndex < len) {
          e && e.preventDefault()
          this.select(this.suggestions[this.highlightedIndex])
          return
        }
        this.suggestions = []
        this.highlightedIndex = -1
        this.showEmptyText = false
        this.showLoadingIcon = true
        this.currentInputValue = value

        this.debouncedGetData(value, e.ifScanGun)
        this.$emit('enter', e)
      },

      /************ 商品弹框相关逻辑处理 ************/
      /** 快捷键中心：dialog 层独占，避免与 page 层 Mousetrap 双绑 body */
      bindSuggestionDialogShortcuts() {
        this.bindDialogShortcuts(
          {
            up: this.selectUp,
            down: this.selectDown,
            tab: this.focusInput,
            enter: (e) => this.handleSelectKeyEnter(e || { preventDefault: () => {} }),
            esc: () => {
              this.suggestionsDialogVisible = false
            },
            f7: this.onSearchMore,
          },
          { onScan: this.handleDialogScanInput }
        )
      },

      /** 选择弹窗打开时的扫码：写入二次搜索框并触发查询（中心模式 onScan 契约） */
      handleDialogScanInput(code) {
        if (!code) return
        this.secondSearchValue = code
        this.$nextTick(() => this.focusInput())
        this.handleInputEnter({
          preventDefault: () => {},
          target: { value: code },
          ifScanGun: true,
        })
      },

      keyboard(Mousetrap) {
        let _this = this
        let keyboardEvent = {
          ArrowUp: {
            key: 'up',
            event: this.selectUp,
          },
          ArrowDown: {
            key: 'down',
            event: this.selectDown,
          },
          Tab: {
            key: 'tab',
            event: this.focusInput,
          },
          Enter: {
            key: 'enter',
            event: this.handleSelectKeyEnter,
          },
          Escape: {
            key: 'esc',
            event: function () {
              _this.suggestionsDialogVisible = false
            },
          },
          F7: {
            key: 'f7',
            event: this.onSearchMore,
          },
        }
        this.mousetrap = Mousetrap(document.body)
        this.mousetrap.bind(
          Object.values(keyboardEvent).map((item) => item.key),
          function (e) {
            keyboardEvent[e.key].event(e)
            return false
          }
        )
      },

      /**
       * 快捷键解绑
       */
      keyboardUnbind() {
        if (this.useShortcutCenter) {
          this.unbindDialogShortcuts()
          return
        }
        if (this.mousetrap) {
          this.mousetrap.unbind(['up', 'down', 'tab', 'enter', 'esc', 'f7'])
          this.mousetrap.destroy()
          this.mousetrap = null
        }
      },

      onSuggestionsDialogOpened() {
        sendWebFunnyEvent(WebFunnyEventCode.PAGE, {
          actionType: `快速添加-选择弹框-打开`,
        })
        this.focusInput()
        this.highlightRow(0)
        this.isSelected = false
        if (this.useShortcutCenter) {
          this.bindSuggestionDialogShortcuts()
        } else {
          this.keyboard(this.$MGUI.Mousetrap)
        }
        this.$emit('suggestions-dialog-open')
      },

      onSuggestionsDialogClosed() {
        sendWebFunnyEvent(WebFunnyEventCode.PAGE, {
          actionType: `快速添加-选择弹框-关闭`,
        })
        this.secondSearchValue = ''
        this.currentSecondSearchValue = ''
        this.suggestionsDialogVisible = false
        this.$emit('suggestions-dialog-close', this.isSelected)
        this.$nextTick(() => {
          this.suggestions = []
          this.highlightedIndex = -1
          this.keyboardUnbind()
        })
      },

      focusInput() {
        this.$refs.searchRef.focus()
      },

      onRowClick({ rowIndex }) {
        this.highlightedIndex = rowIndex
      },

      // 双击选择
      onRowDbclick({ rowIndex }) {
        if (!this.canEdit) {
          this.$mgMessage(this.$MGUI.i18n.t('您暂无编辑商品资料的权限，无法进行编辑'), 'error')
          return
        }
        this.highlightedIndex = rowIndex
        this.handleFinallySelect()
      },

      // 回车选中
      handleSelectKeyEnter(e) {
        e.preventDefault()
        if (!this.canEdit) {
          this.$mgMessage(this.$MGUI.i18n.t('您暂无编辑商品资料的权限，无法进行编辑'), 'error')
          return
        }
        if (this.suggestions.length) {
          this.handleFinallySelect()
        }
      },

      handleFinallySelect() {
        if (!this.canEdit) {
          this.$mgMessage(this.$MGUI.i18n.t('您暂无编辑商品资料的权限，无法进行编辑'), 'error')
          return
        }
        this.isSelected = true
        this.handleKeyEnter({
          preventDefault: () => {},
          target: {
            value: this.$refs.input.getInput().value,
          },
        })
        this.suggestionsDialogVisible = false
      },

      selectUp() {
        if (this.suggestions?.length > 0) {
          this.highlightRow(this.highlightedIndex - 1)
          return
        }
      },

      selectDown() {
        if (this.suggestions?.length > 0) {
          this.highlightRow(this.highlightedIndex + 1)
          return
        }
      },

      handleInputEnter(e) {
        const len = this.suggestions?.length
        const value = e.target.value
        if (
          this.currentSecondSearchValue === value &&
          len > 0 &&
          this.highlightedIndex >= 0 &&
          this.highlightedIndex < len
        ) {
          e && e.preventDefault()
          this.handleFinallySelect()
          return
        }
        sendWebFunnyEvent(WebFunnyEventCode.PAGE, {
          actionType: `快速添加-选择弹框-搜索(${value})`,
        })
        // 接口二次搜索
        this.highlightedIndex = -1
        this.tableLoading = true
        this.currentSecondSearchValue = value
        this.getData(this.$refs.input.getInput().value)
      },

      highlightRow(index) {
        if (!this.hideSuggestion) return
        if (!this.suggestionsDialogVisible) return

        if (index < 0) {
          if (this.suggestions.length) {
            this.highlightedIndex = 0
            return
          }
          this.highlightedIndex = -1
          return
        }
        if (index >= this.suggestions.length) {
          index = this.suggestions.length - 1
        }

        this.highlightedIndex = index
        this.$refs.mgVxeTable.setCurrentRow(this.suggestions[index])
        this.$refs.mgVxeTable.scrollToRow(this.suggestions[index])
      },
      onSearchMore() {
        this.$nextTick(() => {
          this.suggestionsDialogVisible = false
          this.$emit('search-more')
        })
      },
      onContinueAdd() {
        this.$nextTick(() => {
          this.suggestionsDialogVisible = false
          this.$emit('continue-add')
        })
      },
    },

    beforeDestroy() {
      this.$refs.suggestions?.$destroy()
    },
  }
</script>
<style lang="scss" scoped>
  .el-autocomplete-suggestion li {
    padding: 0;
  }
  ::v-deep(.el-autocomplete-suggestion__wrap) {
    padding: 0;
  }
  .search-more-goods-btn {
    float: right;
    margin-right: 48px;
  }
</style>
<style lang="scss">
  $border-color: rgba(37, 42, 61, 0.1);
  .mg-autocomplate-td,
  .mg-autocomplate-th {
    display: flex;
    align-items: center;
    background: rgba(37, 42, 61, 0.05);
    span {
      width: 100px;
      padding: 0 8px;
      height: 32px;
      line-height: 32px;
      border-right: 1px solid $border-color;
      font-weight: 600;
      font-size: 18px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      &:last-child {
        border-right: none;
      }
    }
  }
  .mg-autocomplate-td {
    background: #fff;
    border-bottom: 1px solid $border-color;
    span {
      font-weight: normal;
    }
  }
  .mg-autocomplete-suggestion__empty {
    min-height: 30px;
    text-align: center;
    line-height: 30px;
  }
  .el-icon-loading.is-loading {
    color: $mg--color-primary;
  }
  .mg-fast-add-goods-dialog {
    .el-dialog__header {
      padding: 16px 24px;
      border-bottom: none;
      .el-dialog__headerbtn {
        top: 20px;
        color: #000;
        .el-dialog__close {
          font-size: 28px;
          color: #000;
          font-weight: 600;
        }
      }
    }
    .el-dialog__body {
      padding: 0 12px;
      height: 580px;

      .vxe-table--loading {
        .vxe-loading--chunk {
          color: $mg--color-primary;
        }
      }

      .suggestion-dialog__tips {
        margin-left: 10px;
        font-weight: normal;
        color: rgb(51, 51, 51);
      }
    }
  }
</style>
